<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="flatParticipantRows"
      :items-per-page="-1"
      hide-default-footer
      dense
      class="pdp-feedback-table"
    >
      <template v-slot:[`header.participants`]="{ header }">
        <div class="header-with-action">
          <span class="header-text header-text--left">{{ header.text }}</span>
          <v-btn
            v-if="selectedMeetingForActions && canEditMeeting(selectedMeetingForActions)"
            small
            icon
            class="header-action-btn"
            @click.stop="showAddParticipantDialog(selectedMeetingForActions)"
          >
            <v-icon small class="primary--text">mdi-plus</v-icon>
          </v-btn>
        </div>
      </template>
      <template v-slot:body="{ items }">
        <tbody>
          <tr
            v-for="row in items"
            :key="row.rowKey"
            class="participant-row"
          >
              <td class="participant-td">
                <div class="participant-name-cell">
                  <span>{{ row.participant.Full_Name }}</span>
                  <div class="participant-actions">
                    <v-btn
                      v-if="canEditParticipant(row.participant, row.meeting) && row.participant.Employee_ID !== employee.Employee_ID"
                      icon
                      x-small
                      class="delete-icon-btn"
                      @click="removeParticipant(row.participant, row.meeting)"
                    >
                      <v-icon class="action-delete-icon">mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>
                </div>
              </td>

              <td class="role-td">
                <v-select
                  v-if="canEditParticipant(row.participant, row.meeting)"
                  v-model="row.participant.selectedRole"
                  :items="availableRoles"
                  append-icon=""
                  dense
                  flat
                  solo
                  hide-details
                  placeholder="Role"
                  class="role-select"
                  @change="updateParticipantRole(row.participant)"
                >
                  <template v-slot:selection="{ item }">
                    <div class="dropdown-pill dropdown-pill--role">
                      <span class="dropdown-pill__text">{{ item || 'Role' }}</span>
                      <v-icon small class="dropdown-pill__icon">mdi-menu-down</v-icon>
                    </div>
                  </template>
                </v-select>
                <v-select
                  v-else
                  :value="row.participant.selectedRole || row.participant.Roles || ''"
                  :items="row.participant.selectedRole || row.participant.Roles ? [row.participant.selectedRole || row.participant.Roles] : []"
                  append-icon=""
                  dense
                  flat
                  solo
                  hide-details
                  placeholder="Role"
                  class="role-select"
                  disabled
                >
                  <template v-slot:selection="{ item }">
                    <div class="dropdown-pill dropdown-pill--role">
                      <span class="dropdown-pill__text">{{ item || 'Role' }}</span>
                      <v-icon small class="dropdown-pill__icon">mdi-menu-down</v-icon>
                    </div>
                  </template>
                </v-select>
              </td>

              <td class="feedback-td">
                <v-select
                  v-if="canEditParticipant(row.participant, row.meeting)"
                  v-model="row.participant.Feedback"
                  :items="feedbackOptions.filter(f => f !== 'Feedback')"
                  append-icon=""
                  dense
                  flat
                  solo
                  hide-details
                  placeholder="Feedback"
                  class="feedback-select"
                  @change="updateParticipantFeedback(row.participant)"
                >
                  <template v-slot:item="{ item }">
                    <v-chip :color="getFeedbackColor(item)" small text-color="white">{{ item }}</v-chip>
                  </template>
                  <template v-slot:selection="{ item }">
                    <div :class="['dropdown-pill', getFeedbackPillClass(item)]">
                      <span class="dropdown-pill__text">{{ item || 'Feedback' }}</span>
                      <v-icon small class="dropdown-pill__icon">mdi-menu-down</v-icon>
                    </div>
                  </template>
                </v-select>
                <v-select
                  v-else
                  :value="row.participant.Feedback || 'Feedback'"
                  :items="row.participant.Feedback ? [row.participant.Feedback] : ['Feedback']"
                  append-icon=""
                  dense
                  flat
                  solo
                  hide-details
                  class="feedback-select"
                  disabled
                >
                  <template v-slot:selection="{ item }">
                    <div :class="['dropdown-pill', getFeedbackPillClass(item)]">
                      <span class="dropdown-pill__text">{{ item || 'Feedback' }}</span>
                      <v-icon small class="dropdown-pill__icon">mdi-menu-down</v-icon>
                    </div>
                  </template>
                </v-select>
              </td>

              <td class="notes-td">
                <div class="notes-cell">
                  <div class="notes-content">
                    <div
                      v-if="getCurrentParticipantNote(row.participant) && addingNoteParticipantId !== row.participant.Participant_ID"
                      class="notes-list"
                    >
                      <span
                        :key="getCurrentParticipantNote(row.participant).Note_ID || row.participant.Participant_ID"
                        class="note-comment"
                        v-html="formatNoteHtml(getCurrentParticipantNote(row.participant).Note_Text)"
                      ></span>
                    </div>
                    <v-textarea
                      v-if="canEditParticipant(row.participant, row.meeting) && addingNoteParticipantId === row.participant.Participant_ID"
                      :ref="'noteInput-' + row.participant.Participant_ID"
                      :value="noteDrafts[row.participant.Participant_ID] || ''"
                      placeholder="Note"
                      rows="1"
                      auto-grow
                      dense
                      flat
                      solo
                      hide-details
                      class="notes-inline-input"
                      @input="setNoteDraft(row.participant.Participant_ID, $event)"
                      @blur="onNoteBlur(row.participant)"
                      @keydown.enter.exact.prevent="onNoteBlur(row.participant)"
                    />
                    <div v-else-if="!getCurrentParticipantNote(row.participant)" class="no-notes">-</div>
                  </div>
                  <v-btn
                    v-if="canEditParticipant(row.participant, row.meeting) && addingNoteParticipantId !== row.participant.Participant_ID"
                    small
                    icon
                    class="add-note-btn"
                    @click="showNewNoteField(row.participant)"
                  >
                  <v-icon class="action-pencil-icon">mdi-pencil</v-icon>
                  </v-btn>
                </div>
              </td>
          </tr>
        </tbody>
      </template>
    </v-data-table>

    <v-dialog v-model="showParticipantDialog" max-width="600">
      <v-card>
        <v-card-title>Add Participant</v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="selectedEmployee"
            :items="availableEmployees"
            item-text="full_name"
            item-value="employee_id"
            label="Select Employee"
            solo
            dense
            :search-input.sync="employeeSearch"
            :loading="loadingEmployees"
          >
            <template v-slot:item="{ item }">
              <div>{{ item.full_name }}</div>
            </template>
          </v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeParticipantDialog">Cancel</v-btn>
          <v-btn color="primary" :disabled="!selectedEmployee" @click="addParticipant">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmOrDeniedDialog
      v-if="showDeleteParticipantDialog"
      v-model="showDeleteParticipantDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      :message="currentParticipantToRemove ? `Are you sure you want to remove ${currentParticipantToRemove.participant.Full_Name} from this meeting?` : ''"
      @onDeleteClick="confirmRemoveParticipant"
      @onCancelClick="showDeleteParticipantDialog = false; currentParticipantToRemove = null"
      @onCloseClick="showDeleteParticipantDialog = false; currentParticipantToRemove = null"
    />

  </div>
</template>

<script>
import { getApiErrorMessage } from '@/utils/apiError'
import { formatMeansText } from '@/utils/linkifyText'
export default {
  name: 'PdpFeedbackTab',
  components: {
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    employee: {
      required: true,
      type: Object
    },
    rolesOfUser: {
      required: false,
      type: Array,
      default: () => []
    },
    selectedMeeting: {
      required: false,
      type: Object,
      default: null
    }
  },
  data () {
    return {
      headers: [
        { text: 'Participants', value: 'participants', sortable: false, width: '220px', align: 'left' },
        { text: 'Role', value: 'roles', sortable: false, width: '130px', align: 'center' },
        { text: 'Feedback', value: 'feedback', sortable: false, width: '130px', align: 'center' },
        { text: 'Notes', value: 'notes', sortable: false, align: 'left' }
      ],
      meetings: [],
      participants: {},
      noteDrafts: {},
      noteSaveLocks: {},
      addingNoteParticipantId: null,
      showParticipantDialog: false,
      currentMeeting: null,
      selectedEmployee: null,
      availableEmployees: [],
      employeeSearch: '',
      loadingEmployees: false,
      showDeleteParticipantDialog: false,
      currentParticipantToRemove: null,
      availableRoles: [
        'Employee',
        'Supervisor',
        'Mentor',
        'Project Manager',
        'Tech Lead',
        'Team Lead',
        'Team Member',
        'HR Manager',
        'Account Manager'
      ],
      feedbackOptions: ['Feedback', 'Positive', 'Neutral', 'Negative', 'Not Applicable']
    }
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    },
    isHRorFinancierOrManager () {
      return this.rolesOfUser.some(role => role.RoleName === 'HR' || role.RoleName === 'financier' || role.RoleName === 'manager')
    },
    isCurrentEmployee () {
      return this.employee && this.user && this.employee.Employee_ID === this.user.employee_id
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    meetingsWithParticipants () {
      const selectedMeetingId = this.selectedMeeting && this.selectedMeeting.Meeting_ID
        ? String(this.selectedMeeting.Meeting_ID)
        : null
      const meetings = selectedMeetingId
        ? this.meetings.filter(meeting => String(meeting.Meeting_ID) === selectedMeetingId)
        : this.meetings
      return meetings.map(meeting => {
        const meetingParticipants = this.participants[meeting.Meeting_ID] || []
        return {
          ...meeting,
          participants: meetingParticipants.map(p => {
            return {
              ...p,
              selectedRole: p.Roles || this.getSuggestedRoleForParticipant(p) || null,
              notes: p.notes || []
            }
          })
        }
      })
    },
    flatParticipantRows () {
      const rows = []
      for (const meeting of this.meetingsWithParticipants) {
        const participants = meeting.participants || []
        participants.forEach((participant) => {
          rows.push({
            rowKey: `${meeting.Meeting_ID}-${participant.Participant_ID}`,
            meeting,
            participant
          })
        })
        if (participants.length === 0) {
          rows.push({
            rowKey: `${meeting.Meeting_ID}-empty`,
            meeting,
            participant: { Full_Name: '—', notes: [] }
          })
        }
      }
      return rows
    },
    selectedMeetingForActions () {
      if (!this.selectedMeeting || !this.selectedMeeting.Meeting_ID) {
        return this.meetingsWithParticipants.length ? this.meetingsWithParticipants[0] : null
      }
      const selectedMeetingId = String(this.selectedMeeting.Meeting_ID)
      return this.meetingsWithParticipants.find(meeting => String(meeting.Meeting_ID) === selectedMeetingId) || null
    }
  },
  async mounted () {
    if (this.employee && this.employee.Employee_ID) {
      await this.loadMeetings()
    }
  },
  methods: {
    emitPdpPeriodMetadataChanged () {
      this.$emit('pdp-period-metadata-changed')
    },
    formatNoteHtml (value) {
      return formatMeansText(value)
    },
    getParticipantSource (participant) {
      if (!participant || participant.Participant_ID == null || participant.Meeting_ID == null) return null
      const list = this.participants[participant.Meeting_ID]
      if (!Array.isArray(list)) return null
      return list.find(p => p.Participant_ID === participant.Participant_ID) || null
    },
    canEditParticipant (participant, meeting) {
      if (!participant || !meeting) return false
      const isOwnData = participant.Employee_ID === this.user.employee_id
      return this.isHRorFinancierOrManager || (isOwnData && this.isCurrentEmployee)
    },
    canEditMeeting (meeting) {
      if (!meeting) return false
      return this.isHRorFinancierOrManager
    },
    async loadMeetings () {
      try {
        const response = await this.axios.get(`/api/pdp/meetings/${this.employee.Employee_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        this.meetings = response.data

        await Promise.all(
          this.meetings.map(async (meeting) => {
            try {
              await this.loadParticipants(meeting.Meeting_ID)
            } catch (error) {
              console.error(`Error loading participants for meeting ${meeting.Meeting_ID}:`, error)
            }
          })
        )
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    getSuggestedRoleForParticipant (participant) {
      if (!participant || !this.employee) return null
      const empId = participant.Employee_ID
      if (empId === this.employee.Employee_ID) return 'Employee'
      if (this.employee.Supervisor_ID && empId === this.employee.Supervisor_ID) return 'Supervisor'
      if (this.employee.Mentor_ID && empId === this.employee.Mentor_ID) return 'Mentor'
      return null
    },
    async loadParticipants (meetingId) {
      try {
        const response = await this.axios.get(`/api/pdp/meetings/${meetingId}/participants`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        const participants = response.data
        this.$set(this.participants, meetingId, participants)

        for (const participant of participants) {
          await this.loadParticipantNotes(participant)
        }
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    async loadParticipantNotes (participant) {
      try {
        const response = await this.axios.get(`/api/pdp/participants/${participant.Participant_ID}/notes`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        const notes = Array.isArray(response.data) ? response.data : []
        const src = this.getParticipantSource(participant) || participant
        this.$set(src, 'notes', notes)
      } catch (_) {
        const src = this.getParticipantSource(participant) || participant
        this.$set(src, 'notes', [])
      }
    },
    async updateParticipantRole (participant) {
      try {
        await this.axios.put(`/api/pdp/participants/${participant.Participant_ID}/roles`, {
          roles: participant.selectedRole || null
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        const srcRole = this.getParticipantSource(participant)
        if (srcRole) {
          this.$set(srcRole, 'Roles', participant.selectedRole || null)
        }
        this.$store.dispatch('setSuccess', { successMsg: 'Role updated successfully' })
        this.emitPdpPeriodMetadataChanged()
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    async updateParticipantFeedback (participant) {
      try {
        await this.axios.put(`/api/pdp/participants/${participant.Participant_ID}/feedback`, {
          feedback: participant.Feedback
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        const srcFb = this.getParticipantSource(participant)
        if (srcFb) {
          this.$set(srcFb, 'Feedback', participant.Feedback)
        }
        this.$store.dispatch('setSuccess', { successMsg: 'Feedback updated successfully' })
        this.emitPdpPeriodMetadataChanged()
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    setNoteDraft (participantId, value) {
      this.$set(this.noteDrafts, participantId, value)
    },
    getCurrentParticipantNote (participant) {
      if (!participant || !Array.isArray(participant.notes) || !participant.notes.length) return null
      return participant.notes[0]
    },
    showNewNoteField (participant) {
      if (!participant || !participant.Participant_ID) return
      if (this.addingNoteParticipantId === participant.Participant_ID) {
        this.$nextTick(() => {
          const ref = this.$refs['noteInput-' + participant.Participant_ID]
          const comp = Array.isArray(ref) ? ref[0] : ref
          if (comp && comp.$el) {
            const textarea = comp.$el.querySelector('textarea')
            if (textarea) textarea.focus()
          }
        })
        return
      }
      this.addingNoteParticipantId = participant.Participant_ID
      const currentNote = this.getCurrentParticipantNote(participant)
      this.$set(this.noteDrafts, participant.Participant_ID, currentNote ? (currentNote.Note_Text || '') : '')
      this.$nextTick(() => {
        const ref = this.$refs['noteInput-' + participant.Participant_ID]
        const comp = Array.isArray(ref) ? ref[0] : ref
        if (comp && comp.$el) {
          const textarea = comp.$el.querySelector('textarea')
          if (textarea) textarea.focus()
        }
      })
    },
    async onNoteBlur (participant) {
      if (!participant || !participant.Participant_ID) return
      const participantId = participant.Participant_ID
      if (this.noteSaveLocks[participantId]) return
      this.$set(this.noteSaveLocks, participantId, true)
      try {
        const text = typeof this.noteDrafts[participantId] === 'string' ? this.noteDrafts[participantId] : ''
        const currentNote = this.getCurrentParticipantNote(participant)
        const currentText = ((currentNote && currentNote.Note_Text) || '')
        this.addingNoteParticipantId = null
        if (text === currentText) return
        await this.saveParticipantNote(participant, text)
      } finally {
        this.$set(this.noteSaveLocks, participantId, false)
      }
    },
    async saveParticipantNote (participant, noteText) {
      if (!participant || !participant.Participant_ID) return
      const text = typeof noteText === 'string' ? noteText : ''
      try {
        await this.axios.post(`/api/pdp/participants/${participant.Participant_ID}/notes`, {
          noteText: text
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadParticipantNotes(participant)
        this.$store.dispatch('setSuccess', { successMsg: 'Note saved successfully' })
        this.emitPdpPeriodMetadataChanged()
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    async showAddParticipantDialog (meeting) {
      this.currentMeeting = meeting
      this.selectedEmployee = null
      this.employeeSearch = ''
      await this.loadEmployees()
      this.showParticipantDialog = true
    },
    closeParticipantDialog () {
      this.showParticipantDialog = false
      this.currentMeeting = null
      this.selectedEmployee = null
      this.employeeSearch = ''
      this.availableEmployees = []
    },
    async loadEmployees () {
      try {
        this.loadingEmployees = true
        const response = await this.axios.get('/api/employeelist', {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        const existingParticipantIds = new Set(
          (this.currentMeeting.participants || []).map(p => p.Employee_ID)
        )
        const allEmployees = Array.isArray(response.data) ? response.data : []

        const isCurrentEmployeeParticipant = existingParticipantIds.has(this.employee.Employee_ID)

        const availableEmployees = allEmployees.filter(emp =>
          emp.employee_id && !existingParticipantIds.has(emp.employee_id)
        )

        if (!isCurrentEmployeeParticipant && this.employee.Employee_ID) {
          const currentEmployeeInList = allEmployees.find(emp => emp.employee_id === this.employee.Employee_ID)
          if (!currentEmployeeInList) {
            availableEmployees.push({
              employee_id: this.employee.Employee_ID,
              full_name: this.employee.Full_Name || this.employee.Employee_Name,
              login: this.employee.Login || ''
            })
          } else {
            availableEmployees.push(currentEmployeeInList)
          }
        }

        this.availableEmployees = availableEmployees
      } catch (error) {
        console.error('Error loading employees:', error)
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
        this.availableEmployees = []
      } finally {
        this.loadingEmployees = false
      }
    },
    async addParticipant () {
      if (!this.selectedEmployee || !this.currentMeeting) return

      try {
        await this.axios.post(`/api/pdp/meetings/${this.currentMeeting.Meeting_ID}/participants`, {
          employeeId: this.selectedEmployee
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadParticipants(this.currentMeeting.Meeting_ID)
        this.closeParticipantDialog()
        this.$store.dispatch('setSuccess', { successMsg: 'Participant added successfully' })
        this.emitPdpPeriodMetadataChanged()
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    removeParticipant (participant, meeting) {
      this.currentParticipantToRemove = { participant, meeting }
      this.showDeleteParticipantDialog = true
    },
    async confirmRemoveParticipant () {
      if (!this.currentParticipantToRemove) return

      const { participant, meeting } = this.currentParticipantToRemove
      try {
        await this.axios.delete(`/api/pdp/participants/${participant.Participant_ID}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })
        await this.loadParticipants(meeting.Meeting_ID)
        this.showDeleteParticipantDialog = false
        this.currentParticipantToRemove = null
        this.$store.dispatch('setSuccess', { successMsg: 'Participant removed successfully' })
        this.emitPdpPeriodMetadataChanged()
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    getFeedbackColor (feedback) {
      if (!feedback || feedback === 'Feedback') return 'grey'
      const colors = {
        Positive: 'green',
        Neutral: 'orange',
        Negative: 'red',
        'Not Applicable': 'purple'
      }
      return colors[feedback] || 'grey'
    },
    getFeedbackPillClass (feedback) {
      if (!feedback || feedback === 'Feedback') return 'dropdown-pill--feedback-empty'
      const classMap = {
        Positive: 'dropdown-pill--feedback-positive',
        Neutral: 'dropdown-pill--feedback-neutral',
        Negative: 'dropdown-pill--feedback-negative',
        'Not Applicable': 'dropdown-pill--feedback-not-applicable'
      }
      return classMap[feedback] || 'dropdown-pill--feedback-empty'
    }
  }
}
</script>

<style scoped>
.pdp-feedback-table {
  background: var(--v-surface-base);
  border-radius: 4px;
}

.header-with-action {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
}

.header-text {
  display: block;
  min-width: 0;
}

.header-text--left {
  text-align: left;
}

.header-action-btn {
  width: 24px;
  height: 24px;
  min-width: 24px;
}

.action-pencil-icon {
  color: #1976d2 !important;
  font-size: 18px !important;
}

.action-delete-icon {
  color: #e53935 !important;
  font-size: 18px !important;
}

.delete-icon-btn {
  width: 24px !important;
  height: 24px !important;
  min-width: 24px !important;
  margin: 0 !important;
  flex-shrink: 0;
}

.pdp-feedback-table >>> .v-data-table__wrapper {
  border: none;
  border-radius: 4px;
  overflow-x: auto;
}

.pdp-feedback-table >>> table {
  border-collapse: collapse;
  width: 100%;
  table-layout: fixed;
}

.pdp-feedback-table >>> table thead th:nth-child(1),
.pdp-feedback-table >>> table tbody td:nth-child(1) {
  width: 220px;
}

.pdp-feedback-table >>> table thead th:nth-child(2),
.pdp-feedback-table >>> table tbody td:nth-child(2),
.pdp-feedback-table >>> table thead th:nth-child(3),
.pdp-feedback-table >>> table tbody td:nth-child(3) {
  width: 130px;
}

.pdp-feedback-table >>> table thead th:nth-child(4),
.pdp-feedback-table >>> table tbody td:nth-child(4) {
  width: auto;
}

.pdp-feedback-table >>> table thead th {
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.theme--dark .pdp-feedback-table >>> table thead th {
  background-color: rgba(255, 255, 255, 0.06) !important;
  color: rgba(255, 255, 255, 0.87) !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.pdp-feedback-table >>> table tbody td {
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
  padding: 4px !important;
}

.theme--dark .pdp-feedback-table >>> table tbody td {
  border-color: rgba(255, 255, 255, 0.12) !important;
  background-color: var(--v-surface-base) !important;
  color: rgba(255, 255, 255, 0.87) !important;
}

.theme--dark .pdp-feedback-table .header-text {
  color: rgba(255, 255, 255, 0.87);
}

.participant-row td {
  height: 48px;
}

.pdp-feedback-table >>> tbody tr:hover {
  background: transparent !important;
}

.pdp-feedback-table >>> tbody tr:hover td {
  background-color: #eeeeee !important;
}

.theme--dark .pdp-feedback-table >>> tbody tr:hover td {
  background-color: rgba(255, 255, 255, 0.08) !important;
}

.pdp-feedback-table >>> tbody tr:hover td.date-time-td {
  background: transparent !important;
}

.pdp-feedback-table >>> td.date-time-td {
  padding: 8px !important;
}

.date-time-td {
  vertical-align: middle !important;
}

.date-time-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px 0;
  gap: 4px;
}

.delete-meeting-btn {
  flex-shrink: 0;
  opacity: 0.6;
}

.delete-meeting-btn:hover {
  opacity: 1;
}

.date-time-content {
  display: flex;
  flex-direction: column;
}

.status-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface-rgb, 0, 0, 0), 0.6);
  margin-bottom: 4px;
}

.theme--dark .status-label {
  color: rgba(var(--v-theme-on-surface-rgb, 255, 255, 255), 0.7);
}

.date-text {
  font-size: 0.875rem;
  color: var(--v-theme-on-surface);
}

.action-icons {
  display: flex;
  align-items: center;
}

.action-icon {
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.action-icon:hover {
  opacity: 1;
}

.participant-name-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.participant-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.participant-actions-divider {
  width: 1px;
  height: 16px;
  background: rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
}

.theme--dark .participant-actions-divider {
  background: rgba(255, 255, 255, 0.3);
}

.notes-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
}

.notes-content {
  flex: 1;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.role-select,
.feedback-select {
  font-size: 0.875rem;
}

.role-select,
.feedback-select {
  width: 100%;
}

.role-select >>> .v-input__control,
.feedback-select >>> .v-input__control {
  min-height: 24px !important;
  height: 24px !important;
}

.role-select >>> .v-input__slot,
.feedback-select >>> .v-input__slot {
  margin-bottom: 0 !important;
  padding: 0 !important;
  min-height: 24px !important;
  align-items: center !important;
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
}

.role-select >>> .v-input__slot::before,
.role-select >>> .v-input__slot::after,
.feedback-select >>> .v-input__slot::before,
.feedback-select >>> .v-input__slot::after {
  display: none !important;
}

.role-select >>> .v-input,
.feedback-select >>> .v-input {
  padding-top: 0 !important;
  margin-top: 0 !important;
  width: 100% !important;
  min-width: 0;
}

.role-select >>> .v-input__slot,
.role-select >>> .v-select__selections,
.feedback-select >>> .v-input__slot,
.feedback-select >>> .v-select__selections {
  display: flex !important;
  justify-content: center !important;
}

/* Hide empty v-select input so chip stays centered */
.role-select >>> .v-select__selections input,
.feedback-select >>> .v-select__selections input {
  width: 0 !important;
  min-width: 0 !important;
  flex: 0 0 0 !important;
  padding: 0 !important;
  overflow: hidden !important;
}

.role-select >>> .v-input__append-inner,
.feedback-select >>> .v-input__append-inner {
  display: none !important;
}

.role-select >>> .v-select__selection,
.feedback-select >>> .v-select__selection {
  width: 100%;
  max-width: 100%;
  margin-right: 0 !important;
}

.role-select >>> .v-select__selections,
.feedback-select >>> .v-select__selections {
  padding-left: 0;
}

.role-select >>> .v-select__selections .v-select__selection--comma,
.feedback-select >>> .v-select__selections .v-select__selection--comma {
  padding-left: 0;
}

.dropdown-pill {
  min-height: 28px;
  padding: 0 8px 0 10px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  max-width: 100%;
}

.dropdown-pill__text {
  color: #fff;
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
}

.dropdown-pill__icon {
  color: #fff !important;
  font-size: 16px !important;
  flex-shrink: 0;
}

.dropdown-pill--role {
  background: #3c78cf;
}

.dropdown-pill--feedback-positive {
  background: #43a047;
}

.dropdown-pill--feedback-neutral {
  background: #fb8c00;
}

.dropdown-pill--feedback-negative {
  background: #f44336;
}

.dropdown-pill--feedback-not-applicable {
  background: #8e24aa;
}

.dropdown-pill--feedback-empty {
  background: #9e9e9e;
}

.notes-by-author {
  margin-bottom: 2px;
}

.notes-author-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface-rgb, 0, 0, 0), 0.7);
  margin-bottom: 4px;
}

.theme--dark .notes-author-label {
  color: rgba(var(--v-theme-on-surface-rgb, 255, 255, 255), 0.8);
}

.notes-list {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.note-comment {
  display: inline-flex;
  align-items: flex-start;
  gap: 4px;
  font-size: 0.875rem;
  color: var(--v-theme-on-surface);
  padding: 2px 0;
  margin-right: 8px;
  white-space: pre-line;
  word-break: break-word;
}

.note-comment >>> a {
  color: #1976d2;
  text-decoration: underline;
  word-break: break-all;
}

.note-comment--long {
  cursor: pointer;
}

.note-comment--long:hover {
  opacity: 0.85;
}

.note-comment__delete-btn {
  margin-left: 2px !important;
}

.full-note-text {
  white-space: pre-wrap;
  word-break: break-word;
}

.no-notes {
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface-rgb, 0, 0, 0), 0.5);
  margin-bottom: 4px;
}

.theme--dark .no-notes {
  color: rgba(var(--v-theme-on-surface-rgb, 255, 255, 255), 0.6);
}

.add-note-btn,
.add-participant-btn {
  flex-shrink: 0;
}

.notes-inline-input {
  margin-top: 0;
  font-size: 0.875rem;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.notes-inline-input >>> .v-input {
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
}

.notes-inline-input >>> .v-input__control {
  min-height: 28px !important;
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
}

.notes-inline-input >>> .v-input__slot {
  padding: 0 4px !important;
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
}

.notes-inline-input >>> .v-input__slot::before,
.notes-inline-input >>> .v-input__slot::after {
  display: none !important;
}

.notes-inline-input >>> textarea {
  font-size: 0.875rem !important;
  line-height: 1.35 !important;
  padding: 2px 0 !important;
  white-space: pre-line !important;
  word-break: break-word !important;
  overflow-wrap: anywhere !important;
  overflow-x: hidden !important;
  max-width: 100% !important;
}
</style>
